import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CuratedPromptCard } from "@/components/curated-prompt-card";
import { getPaginatedPrompts, getPromptCategories, getPromptPopularTags } from "@/lib/prompts";

interface PromptsSearch {
  page?: number;
}

export const Route = createFileRoute("/prompts/")({
  validateSearch: (search: Record<string, unknown>): PromptsSearch => {
    return {
      page: Number(search?.page) || 1,
    };
  },

  // SSR loader for initial data
  loader: async () => {
    try {
      // Always fetch page 1 for SSR (pagination handled client-side for now)
      const prompts = await getPaginatedPrompts({
        data: { page: 1, limit: 20 }
      });

      // Use pre-computed aggregates (computed once at build time)
      const categories = await getPromptCategories({ data: undefined });
      const popularTags = await getPromptPopularTags({ data: undefined });

      return { prompts, categories, popularTags };
    } catch (error) {
      console.error("[prompts loader] Error:", error);
      return {
        prompts: {
          data: [],
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        },
        categories: [],
        popularTags: [],
      };
    }
  },

  // SEO meta tags - page-specific
  head: ({ loaderData }) => {
    const totalPrompts = loaderData?.prompts?.pagination?.total || 0;
    const page = loaderData?.prompts?.pagination?.page || 1;
    const hasNext = loaderData?.prompts?.pagination?.hasNext || false;

    return {
      meta: [
        {
          title: "AI Image Prompts Gallery — Free Examples for FLUX 3, GPT Image 2.5 & More | Images AI",
        },
        {
          name: "description",
          content: "Browse curated AI image prompts for product photography, YouTube thumbnails, food photography & more. Copy-paste examples for FLUX 3, GPT Image 2.5, Ideogram V4, and Qwen Image 3.",
        },
        {
          name: "keywords",
          content: "AI prompts, FLUX 3 prompts, GPT Image 2.5 prompts, Ideogram V4 prompts, Qwen Image 3 prompts, text to image prompt examples, product photo prompts, free AI image prompts, youtube thumbnail generator, food photography prompts",
        },
        {
          property: "og:title",
          content: "AI Image Prompts Gallery — Curated Free Examples | Images AI",
        },
        {
          property: "og:description",
          content: "Curated AI image prompts for product photography, YouTube thumbnails, food photography & more. Built for FLUX 3, GPT Image 2.5, Ideogram V4, and Qwen.",
        },
        {
          property: "og:type",
          content: "website",
        },
        {
          property: "og:url",
          content: "https://images-ai.app/prompts",
        },
        {
          property: "twitter:card",
          content: "summary_large_image",
        },
        {
          property: "twitter:title",
          content: "AI Image Prompts Gallery - Curated Examples",
        },
      ],
      links: [
        {
          rel: "canonical",
          href: `https://images-ai.app/prompts${page > 1 ? `?page=${page}` : ''}`,
        },
        ...(page > 1 ? [{
          rel: "prev",
          href: `https://images-ai.app/prompts${page === 2 ? '' : `?page=${page - 1}`}`,
        }] : []),
        ...(hasNext ? [{
          rel: "next",
          href: `https://images-ai.app/prompts?page=${page + 1}`,
        }] : []),
      ],
      // Inject JSON-LD structured data
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AI Image Prompts Gallery",
            "description": "Curated collection of AI image generation prompts",
            "url": "https://images-ai.app/prompts",
            "publisher": {
              "@type": "Organization",
              "name": "Images AI",
              "url": "https://images-ai.app"
            },
            "numberOfItems": totalPrompts,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": loaderData?.prompts?.data?.map((prompt: any, index: number) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "ImageObject",
                "name": prompt.title,
                "url": `https://images-ai.app/prompts/${prompt.slug}`,
                "image": prompt.imageUrl,
                "description": prompt.metaDescription || prompt.prompt,
              }
            })) || [],
          }),
        },
      ],
    };
  },

  component: PromptsPage,
});

function PromptsPage() {
  const navigate = useNavigate();
  const { page = 1 } = Route.useSearch();
  const { prompts: initialPrompts, categories, popularTags } = Route.useLoaderData();
  const { session } = Route.useRouteContext();

  // Fetch prompts with pagination (uses SSR data for page 1)
  const { data: prompts } = useQuery({
    queryKey: ["curated-prompts", page],
    queryFn: () => getPaginatedPrompts({ data: { page, limit: 20 } }),
    initialData: page === 1 ? initialPrompts : undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handlePageChange = (newPage: number) => {
    navigate({ to: "/prompts", search: { page: newPage } });
  };

  // Safety check
  if (!prompts || !prompts.pagination) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Error loading prompts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold">
              AI Prompt Gallery
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground">
              Curated prompts for flagship models — FLUX 3, GPT Image 2.5,
              Ideogram V4, Qwen Image 3, and Seedream 5.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              {session ? (
                <Link to="/dashboard" preload="intent">
                  <Button size="lg" className="rounded-full px-8">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/signup" preload="intent">
                    <Button size="lg" className="rounded-full px-8">
                      Try These Prompts Free
                    </Button>
                  </Link>
                  <Link to="/login" preload="intent">
                    <Button size="lg" variant="outline" className="rounded-full px-8">
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Prompts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Category and Tag Filters */}
          {(categories.length > 0 || popularTags.length > 0) && (
            <div className="mb-12">
              <h2 className="text-sm font-semibold text-muted-foreground mb-4">
                Browse by Category
              </h2>
              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map((cat) => (
                  <Link key={cat} to={`/prompts/category/${cat}`} preload="intent">
                    <Badge
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors px-4 py-2 text-sm capitalize"
                    >
                      {cat}
                    </Badge>
                  </Link>
                ))}
              </div>
              {popularTags.length > 0 && (
                <>
                  <h2 className="text-sm font-semibold text-muted-foreground mb-4 mt-8">
                    Popular Tags
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {popularTags.map((tag) => (
                      <Link key={tag} to={`/prompts/tag/${tag}`} preload="intent">
                        <Badge
                          variant="outline"
                          className="cursor-pointer hover:bg-secondary transition-colors px-3 py-1.5 text-sm"
                        >
                          {tag.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {prompts.data.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No prompts available yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {prompts.data.map((prompt, index) => (
                  <CuratedPromptCard key={prompt.slug} prompt={prompt} index={index} />
                ))}
              </div>

              {/* Pagination */}
              {prompts.pagination.totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    disabled={!prompts.pagination.hasPrev}
                    onClick={() => handlePageChange(page - 1)}
                    className="rounded-full"
                  >
                    Previous
                  </Button>

                  <span className="text-sm text-muted-foreground px-4">
                    Page {prompts.pagination.page} of {prompts.pagination.totalPages}
                  </span>

                  <Button
                    variant="outline"
                    disabled={!prompts.pagination.hasNext}
                    onClick={() => handlePageChange(page + 1)}
                    className="rounded-full"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}

          {/* CTA Section */}
          <div className="mt-20 text-center space-y-6">
            <h2 className="text-3xl font-bold">
              Ready to create your own?
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Generate with FLUX 3, GPT Image 2.5, Qwen Image 3, and Seedream 5. Free to start — no credit card required.
            </p>
            {session ? (
              <Link to="/dashboard">
                <Button size="lg" className="rounded-full px-12">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-12">
                  Get Started Free
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
